import React, { useState } from "react";
import { useFetch } from "@/hooks/useApi";
import api, { formatApiErrorDetail, API } from "@/lib/api";
import { PageHeader, Loader, StatusPill, Plate, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { gbp, num, ukDate, todayISO } from "@/lib/format";
import * as Icons from "lucide-react";
import { toast } from "sonner";

const emptyForm = { van_reg: "", driver_id: "", date: todayISO(), miles_driven: "", litres: "", price_per_litre: "", station: "", full_tank: false, evidence_file_id: "" };

export default function Fuel() {
  const { data: logs, loading, refetch } = useFetch("/fuel");
  const { data: vans } = useFetch("/vans");
  const { data: drivers } = useFetch("/drivers");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [uploading, setUploading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });
  const total = (parseFloat(form.litres) || 0) * (parseFloat(form.price_per_litre) || 0);

  const uploadReceipt = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, evidence_file_id: data.file_id }));
      toast.success("Receipt uploaded");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const save = async () => {
    try {
      await api.post("/fuel", { ...form, receipt_file_id: form.evidence_file_id });
      toast.success("Fuel log added");
      setOpen(false); setForm(emptyForm); refetch();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (id) => { try { await api.delete(`/fuel/${id}`); refetch(); } catch { toast.error("Error"); } };

  if (loading) return <Loader />;
  const flagged = (logs || []).filter((l) => l.variance_status !== "within_target").length;

  return (
    <div className="fp-fade-in">
      <PageHeader title="Fuel Control" subtitle="Efficiency tracking with expected vs actual usage & variance flags" testId="fuel-page">
        <Button onClick={() => setOpen(true)} className="w-full sm:w-auto" data-testid="add-fuel-button"><Icons.Plus className="h-4 w-4 mr-1.5" /> Log Fuel</Button>
      </PageHeader>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card className="p-5"><p className="text-xs uppercase tracking-wider text-muted-foreground">Total Logs</p><p className="font-mono text-2xl font-bold mt-2">{logs?.length || 0}</p></Card>
        <Card className="p-5"><p className="text-xs uppercase tracking-wider text-muted-foreground">Flagged</p><p className="font-mono text-2xl font-bold mt-2 text-amber-400">{flagged}</p></Card>
        <Card className="p-5"><p className="text-xs uppercase tracking-wider text-muted-foreground">Total Fuel Cost</p><p className="font-mono text-2xl font-bold mt-2 text-rose-400">{gbp((logs || []).reduce((a, l) => a + (l.total_cost || 0), 0))}</p></Card>
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Date</TableHead><TableHead>Van</TableHead><TableHead>Miles</TableHead><TableHead>Litres</TableHead>
              <TableHead>mi/L</TableHead><TableHead>Expected L</TableHead><TableHead>Variance</TableHead><TableHead>Cost</TableHead>
              <TableHead>Status</TableHead><TableHead>Receipt</TableHead><TableHead className="text-right">Action</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {(logs || []).map((l) => (
                <TableRow key={l.id} data-testid={`fuel-row-${l.id}`}>
                  <TableCell className="font-mono text-sm">{ukDate(l.date)}</TableCell>
                  <TableCell><Plate reg={l.van_reg} /></TableCell>
                  <TableCell className="font-mono">{num(l.miles_driven)}</TableCell>
                  <TableCell className="font-mono">{l.litres}</TableCell>
                  <TableCell className="font-mono">{l.miles_per_litre}</TableCell>
                  <TableCell className="font-mono text-muted-foreground">{l.expected_litres}</TableCell>
                  <TableCell className={`font-mono ${l.variance_litres > 0 ? "text-amber-400" : "text-emerald-400"}`}>{l.variance_litres > 0 ? "+" : ""}{l.variance_litres}L ({l.variance_pct}%)</TableCell>
                  <TableCell className="font-mono">{gbp(l.total_cost)}</TableCell>
                  <TableCell><StatusPill status={l.variance_status} testId={`fuel-variance-${l.id}`} /></TableCell>
                  <TableCell>{l.receipt_file_id ? <a href={`${API}/files/${l.receipt_file_id}`} target="_blank" rel="noreferrer" className="text-primary text-sm hover:underline">View</a> : "—"}</TableCell>
                  <TableCell className="text-right"><Button variant="ghost" size="icon" onClick={() => remove(l.id)}><Icons.Trash2 className="h-4 w-4 text-rose-400" /></Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {(!logs || logs.length === 0) && <EmptyState icon="Fuel" title="No fuel logs" subtitle="Log fuel to track efficiency." />}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="fuel-dialog">
          <DialogHeader><DialogTitle>Log Fuel Purchase</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Van</Label>
              <Select value={form.van_reg} onValueChange={set("van_reg")}>
                <SelectTrigger className="mt-1.5" data-testid="fuel-van-select"><SelectValue placeholder="Select van" /></SelectTrigger>
                <SelectContent>{(vans || []).map((v) => <SelectItem key={v.id} value={v.registration}>{v.registration} · {v.fuel_efficiency} mi/L</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Driver</Label>
              <Select value={form.driver_id || "none"} onValueChange={(v) => set("driver_id")(v === "none" ? "" : v)}>
                <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select driver" /></SelectTrigger>
                <SelectContent><SelectItem value="none">— None —</SelectItem>{(drivers || []).map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Date</Label><Input type="date" value={form.date} onChange={set("date")} className="mt-1.5" /></div>
            <div><Label>Miles Driven</Label><Input type="number" value={form.miles_driven} onChange={set("miles_driven")} className="mt-1.5" data-testid="fuel-miles-input" /></div>
            <div><Label>Litres Purchased</Label><Input type="number" step="0.01" value={form.litres} onChange={set("litres")} className="mt-1.5" data-testid="fuel-litres-input" /></div>
            <div><Label>Price per Litre (£)</Label><Input type="number" step="0.001" value={form.price_per_litre} onChange={set("price_per_litre")} className="mt-1.5" data-testid="fuel-price-input" /></div>
            <div><Label>Fuel Station</Label><Input value={form.station} onChange={set("station")} className="mt-1.5" /></div>
            <div>
              <Label>Receipt</Label>
              <Input type="file" accept="image/*" onChange={(e) => uploadReceipt(e.target.files[0])} className="mt-1.5" data-testid="fuel-receipt-input" />
              {uploading && <p className="text-xs text-muted-foreground mt-1">Uploading…</p>}
              {form.evidence_file_id && <p className="text-xs text-emerald-400 mt-1">Attached ✓</p>}
            </div>
            <div className="sm:col-span-2 flex items-center gap-2">
              <Checkbox id="fulltank" checked={form.full_tank} onCheckedChange={(v) => set("full_tank")(!!v)} data-testid="fuel-fulltank-checkbox" />
              <Label htmlFor="fulltank" className="cursor-pointer">Full-tank to full-tank fill (accurate efficiency)</Label>
            </div>
          </div>
          <div className="rounded-lg bg-muted p-3 flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Total fuel cost</span>
            <span className="font-mono font-bold text-lg text-primary">{gbp(total)}</span>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-fuel-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
